import React, { useContext, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Sidebar from "../../layout/Sidebar/SideBar";
import Navbar from "../../layout/Navbar/Navbar";
import blogFetch from "../../axios/config";
import Context from "../../context/Context";
import language from "../../nls/language.json";
import { Column, DataTable } from "../fotolito/DataTable";
import "../fotolito/style.css";
import "./RastreabilidadeMateriais.css";

const LOTE_MAX_LENGTH = 50;

// Todos os campos podem vir null do backend.
interface RastreabilidadeItem {
    tipo: string | null;
    mat_Codigo: string | null;
    mat_Classe: string | null;
    lote_Corte: string | null;
    ordem_Serial: string | null;
    ordem_Posicao: string | null;
    lote_Consumo: string | null;
    apontamento_Usuario: string | null;
    lote_Fornecedor: string | null;
    apontamento_Hora: string | null;
}

// "2026-09-30T14:22:05[.537]" → "30/09/2026 14:22:05" (hora local do servidor, sem conversão de fuso).
const formatDateTime = (value: string | null) =>
    value ? value.replace(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2}:\d{2}).*$/, "$3/$2/$1 $4") : "-";

const valorOuTraco = (value: string | null) => value ?? "-";

function mensagemDeErro(error: unknown): string {
    if (!axios.isAxiosError(error)) return "Ocorreu um erro inesperado. Tente novamente.";
    if (!error.response) return "Falha de conexão com o servidor. Tente novamente.";

    switch (error.response.status) {
        case 400:
            return error.response.data?.message ?? `Lote inválido (máx. ${LOTE_MAX_LENGTH} caracteres).`;
        case 401:
            return "Sessão expirada. Faça login novamente.";
        default:
            // A mensagem do 500 é técnica: fica só no console.
            return "Erro ao consultar a rastreabilidade do lote. Tente novamente.";
    }
}

export function RastreabilidadeMateriais(): JSX.Element {
    const { selectedCountry } = useContext(Context);
    const [loteInput, setLoteInput] = useState("");
    const [loteBuscado, setLoteBuscado] = useState("");
    const [dados, setDados] = useState<RastreabilidadeItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const header = (language as Record<string, any>)[selectedCountry.name]
        .Dashboard.pecasSetor.ficha.info.rastreabilidadeSaga.tableHeader;

    const colunas: Column<RastreabilidadeItem>[] = useMemo(() => [
        { key: "ordem_Serial", header: "ORDEM", render: (item) => valorOuTraco(item.ordem_Serial) },
        { key: "tipo", header: header.tipo, render: (item) => valorOuTraco(item.tipo) },
        { key: "mat_Codigo", header: header.codMaterial, render: (item) => valorOuTraco(item.mat_Codigo) },
        { key: "mat_Classe", header: header.classe, render: (item) => valorOuTraco(item.mat_Classe) },
        { key: "lote_Corte", header: header.loteCorte, render: (item) => valorOuTraco(item.lote_Corte) },
        { key: "ordem_Posicao", header: header.posicao, render: (item) => valorOuTraco(item.ordem_Posicao) },
        { key: "lote_Consumo", header: header.loteConsumo, render: (item) => valorOuTraco(item.lote_Consumo) },
        { key: "apontamento_Usuario", header: header.usuario, render: (item) => valorOuTraco(item.apontamento_Usuario) },
        { key: "lote_Fornecedor", header: header.loteFornecedor, render: (item) => valorOuTraco(item.lote_Fornecedor) },
        {
            key: "apontamento_Hora",
            header: header.dataHora,
            render: (item) => formatDateTime(item.apontamento_Hora),
        },
    ], [header]);

    const buscar = async () => {
        const lote = loteInput.trim();
        // Lote vazio cairia na rota de consulta por ordem no backend; bloquear aqui.
        if (!lote || isLoading) return;
        if (lote.length > LOTE_MAX_LENGTH) {
            toast.error(`Lote inválido (máx. ${LOTE_MAX_LENGTH} caracteres).`);
            return;
        }

        setIsLoading(true);
        setLoteBuscado(lote);
        try {
            const { data } = await blogFetch.get<RastreabilidadeItem[]>(
                `/SAGAViewRastreabilidadeOrdem/Lote/${encodeURIComponent(lote)}`
            );
            setDados(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Erro ao buscar rastreabilidade do lote:", error);
            toast.error(mensagemDeErro(error));
            setDados([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Mesmo padrão do ExportDataXlsx (componentes/organisms/IndustryPartsTable), com
    // cabeçalhos e valores iguais aos da grid e o total de linhas ao final.
    const exportarExcel = () => {
        if (dados.length === 0) return;

        const cabecalho = colunas.map(coluna => coluna.header);
        const linhas = dados.map(item =>
            colunas.map(coluna => String(coluna.render ? coluna.render(item) ?? "" : (item as any)[coluna.key] ?? ""))
        );
        const planilhaDados = XLSX.utils.aoa_to_sheet([
            cabecalho,
            ...linhas,
            [],
            ["Total de linhas", dados.length],
        ]);

        planilhaDados["!cols"] = cabecalho.map((titulo, index) => ({
            wch: Math.max(10, titulo.length, ...linhas.map(linha => linha[index].length)),
        }));

        const planilha = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(planilha, planilhaDados, "Rastreabilidade");
        // DIAMESANOHORAMINUTO, ex.: 01/10/2026 14:35 → "011020261435".
        const agora = new Date();
        const dois = (n: number) => String(n).padStart(2, "0");
        const dataHora = `${dois(agora.getDate())}${dois(agora.getMonth() + 1)}${agora.getFullYear()}${dois(agora.getHours())}${dois(agora.getMinutes())}`;

        XLSX.writeFile(planilha, `Rastreabilidade_${loteBuscado.replace(/[\\/:*?"<>|]/g, "_")}_${dataHora}.xlsx`);
    };

    return (
        <div className="d-flex app_">
            <Sidebar />
            <Navbar />
            <div className="flex-grow-1">
                <div className="container-fluid mt-3 app_body rastreabilidade-page">
                    <h4 className="fw-bold text-primary mb-3">Rastreabilidade de Materiais</h4>

                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                <div>
                                    <label className="form-label small fw-semibold text-muted mb-1">
                                        Lote AGP / Lote Fornecedor
                                    </label>
                                    <div className="input-group input-group-sm shadow-sm" style={{ minWidth: 300 }}>
                                        <span className="input-group-text"><SearchIcon fontSize="small" /></span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Digite o lote e pressione Enter..."
                                            value={loteInput}
                                            maxLength={LOTE_MAX_LENGTH}
                                            onChange={(e) => setLoteInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") buscar();
                                            }}
                                            autoFocus
                                        />
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={buscar}
                                            disabled={isLoading || !loteInput.trim()}
                                        >
                                            {isLoading ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                "Buscar"
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    className="btn btn-sm btn-outline-success ms-auto d-flex align-items-center gap-1"
                                    onClick={exportarExcel}
                                    disabled={isLoading || dados.length === 0}
                                    title="Exportar dados da grid para Excel"
                                >
                                    <FileDownloadIcon fontSize="small" />
                                    Exportar Excel
                                </button>
                            </div>
                        </div>
                    </div>

                    {loteBuscado ? (
                        <div className="rastreabilidade-grid">
                            {/* Backend não pagina: todas as linhas são exibidas, com rolagem. */}
                            <DataTable
                                data={dados}
                                columns={colunas}
                                isLoading={isLoading}
                                pageSize={0}
                                emptyMessage="Nenhum registro encontrado para o lote informado"
                            />
                            {!isLoading && (
                                <div className="text-center text-muted small mt-2">
                                    {dados.length} registro(s) para o lote <strong>{loteBuscado}</strong>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-center text-muted py-5">
                            Informe um lote e pressione Enter para consultar.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default RastreabilidadeMateriais;
